import { AxiosError } from "axios";
import Cookies from "js-cookie";
import { useRouter } from "next/navigation";
import { useState, useEffect, useCallback, useMemo } from "react";

import { API_CONFIG, getCookieConfig } from "../config/api";
import { showToast } from "../providers/ToastProvider";
import { User, RegisterUser, LoginUser, UserResponse, RegistrationData, UserRole } from "../types";
import AxiosCaller from "../utils/AxiosCaller";

// Type aliases for backward compatibility
export type LoginCredentials = LoginUser;
export type RegisterCredentials = RegisterUser;
export type LoginResponse = UserResponse;

export interface AuthContextType {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	isAdmin: boolean;
	login: (credentials: LoginUser) => Promise<User>;
	register: (credentials: RegistrationData) => Promise<void>;
	logout: () => void;
	checkAuth: () => Promise<void>;
	updateUser: (updatedUser: User) => void;
}

export const useAuth = (): AuthContextType => {
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const router = useRouter();

	const isAuthenticated = !!user;

	// Check if user is authenticated on mount
	useEffect(() => {
		checkAuth();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const checkAuth = useCallback(async () => {
		try {
			const token = Cookies.get(API_CONFIG.COOKIE_CONFIG.ACCESS_TOKEN);
			console.debug("Checking auth, token from cookie:", token ? "present" : "missing");

			if (!token) {
				setIsLoading(false);
				return;
			}

			// Set the token in AxiosCaller
			AxiosCaller.setAuthToken(token);
			console.debug("Token set in AxiosCaller");

			// Try to get user profile
			const response = await AxiosCaller.get(API_CONFIG.ENDPOINTS.PROFILE);
			console.debug("Profile response:", response.data.data);
			setUser(response.data.data);
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			// Token is invalid, remove it
			Cookies.remove(API_CONFIG.COOKIE_CONFIG.ACCESS_TOKEN);
			AxiosCaller.removeAuthToken();
			showToast.error("Sessione scaduta. Effettua nuovamente il login.");
		} finally {
			setIsLoading(false);
		}
	}, []);

	const login = useCallback(async (credentials: LoginUser): Promise<User> => {
		try {
			setIsLoading(true);
			console.debug("Attempting login with:", { email: credentials.email });

			const response = await AxiosCaller.post<{ data: UserResponse }>(API_CONFIG.ENDPOINTS.LOGIN, credentials);
			console.debug("Login response:", response.data.data);

			const { access_token, user } = response.data.data;

			// Store token in cookie and set in AxiosCaller
			Cookies.set(API_CONFIG.COOKIE_CONFIG.ACCESS_TOKEN, access_token, getCookieConfig());
			console.debug("Token stored in cookie:", Cookies.get(API_CONFIG.COOKIE_CONFIG.ACCESS_TOKEN));
			AxiosCaller.setAuthToken(access_token);
			setUser(user);
			return user;
		} catch (error: unknown) {
			let errorMessage = "Errore durante il login";

			if (error instanceof AxiosError) {
				// Handle different types of Axios errors
				if (error.response?.data?.message) {
					errorMessage = error.response.data.message;
				} else if (error.response?.status === 401) {
					errorMessage = "Credenziali non valide";
				} else if (error.response?.status === 422) {
					errorMessage = "Dati di input non validi";
				} else if (error.response?.status && error.response.status >= 500) {
					errorMessage = "Errore del server. Riprova più tardi.";
				} else if (error.code === "NETWORK_ERROR") {
					errorMessage = "Errore di connessione. Verifica la tua connessione internet.";
				}
			}

			showToast.error(errorMessage);
			throw error;
		} finally {
			setIsLoading(false);
		}
	}, []);

	const register = useCallback(async (credentials: RegistrationData) => {
		try {
			setIsLoading(true);

			// Handle FormData for file uploads
			if (credentials instanceof FormData) {
				// Set proper headers for FormData
				await AxiosCaller.post(API_CONFIG.ENDPOINTS.REGISTER, credentials, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
				});
			} else {
				// Handle regular object data
				await AxiosCaller.post(API_CONFIG.ENDPOINTS.REGISTER, credentials);
			}

			showToast.success("Registrazione completata! Controlla la tua email per verificare l'account.");
		} catch (error: unknown) {
			let errorMessage = "Errore durante la registrazione";

			if (error instanceof AxiosError) {
				// Handle different types of Axios errors
				if (error.response?.data?.message) {
					errorMessage = error.response.data.message;
				} else if (error.response?.status === 409) {
					errorMessage = "Email già registrata";
				} else if (error.response?.status === 422) {
					errorMessage = "Dati di input non validi";
				} else if (error.response?.status && error.response.status >= 500) {
					errorMessage = "Errore del server. Riprova più tardi.";
				} else if (error.code === "NETWORK_ERROR") {
					errorMessage = "Errore di connessione. Verifica la tua connessione internet.";
				}
			}

			showToast.error(errorMessage);
			throw error;
		} finally {
			setIsLoading(false);
		}
	}, []);

	const logout = useCallback(() => {
		Cookies.remove(API_CONFIG.COOKIE_CONFIG.ACCESS_TOKEN);
		AxiosCaller.removeAuthToken();
		setUser({
			role: UserRole.ADMIN,
		} as User);
		router.push("/login");
	}, [router]);

	const updateUser = useCallback((updatedUser: User) => {
		setUser(updatedUser);
	}, []);

	const isAdmin = useMemo(() => {
		return user?.role === UserRole.ADMIN;
	}, [user]);

	return {
		user,
		isAuthenticated,
		isAdmin,
		isLoading,
		login,
		register,
		logout,
		checkAuth,
		updateUser,
	};
};
